import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getInitials, getAvatarColor } from '../../lib/utils';
import { Sun, Moon, User, LogOut, Globe, Scroll, Menu, Shield, Search, X } from 'lucide-react';
import { WyrdMakerLogo } from '../ui/WyrdMakerLogo';
import { Sidebar } from './Sidebar';
import { NotificationBell } from './NotificationBell';
import { BugReportButton } from './BugReportButton';

export const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout, language, switchLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const location = useLocation();
  const [navOpen, setNavOpen] = React.useState(false);

  React.useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <Sheet open={navOpen} onOpenChange={setNavOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 lg:hidden"
                    aria-label={language === 'fr' ? 'Ouvrir la navigation' : 'Open navigation'}
                    data-testid="mobile-nav-trigger"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72 sm:w-80 flex flex-col overflow-hidden">
                  <Sidebar
                    campaignId={campaignId}
                    variant="mobile"
                    className="w-full border-0 h-full flex flex-col"
                    scrollAreaClassName="flex-1 min-h-0"
                  />
                </SheetContent>
              </Sheet>
            )}
            <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center group hover:opacity-90 transition-opacity" data-testid="navbar-logo">
              <WyrdMakerLogo size="sm" showText={true} className="group-hover:scale-[1.02] transition-transform" textClassName="hidden sm:block" />
            </Link>

            {/* Public navigation links (desktop) */}
            {!isAuthenticated && (
              <nav className="hidden md:flex items-center gap-1 ml-6">
                {[
                  { to: '/features', label: t('nav_features', language) },
                  { to: '/pricing', label: t('nav_pricing', language) },
                  { to: '/updates', label: t('nav_updates', language) },
                  { to: '/help', label: t('nav_help', language) },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                      location.pathname === link.to
                        ? 'text-foreground font-medium bg-secondary/50'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/30'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Search / Command Palette trigger */}
            {isAuthenticated && campaignId && (
              <Button
                variant="outline"
                className="hidden sm:inline-flex items-center gap-2 h-9 px-3 text-sm text-muted-foreground"
                onClick={() => window.__openCommandPalette?.()}
                data-testid="search-trigger"
              >
                <Search className="h-4 w-4" />
                <span>{t('command_palette_search_hint', language)}</span>
                <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>
            )}

            {/* Public mobile nav trigger */}
            {!isAuthenticated && (
              <Sheet open={navOpen} onOpenChange={setNavOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 md:hidden"
                    aria-label={language === 'fr' ? 'Ouvrir le menu' : 'Open menu'}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-64 p-6">
                  <nav className="flex flex-col gap-1 mt-4">
                    {[
                      { to: '/features', label: t('nav_features', language) },
                      { to: '/pricing', label: t('nav_pricing', language) },
                      { to: '/updates', label: t('nav_updates', language) },
                      { to: '/help', label: t('nav_help', language) },
                      { to: '/contact', label: t('nav_contact', language) },
                    ].map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`text-sm px-3 py-2.5 rounded-md transition-colors ${
                          location.pathname === link.to
                            ? 'text-foreground font-medium bg-secondary/50'
                            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/30'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    <div className="border-t border-border mt-3 pt-3 flex flex-col gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { navigate('/login'); setNavOpen(false); }} className="justify-start">
                        {t('login', language)}
                      </Button>
                      <Button size="sm" onClick={() => { navigate('/register'); setNavOpen(false); }} className="justify-start">
                        {t('register', language)}
                      </Button>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            )}

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="language-toggle">
                  <Globe className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => switchLanguage('fr')} data-testid="lang-fr">
                  <span className={language === 'fr' ? 'font-semibold' : ''}>🇫🇷 Français</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => switchLanguage('en')} data-testid="lang-en">
                  <span className={language === 'en' ? 'font-semibold' : ''}>🇬🇧 English</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Bug Report */}
            {isAuthenticated && <BugReportButton />}

            {/* Notifications */}
            {isAuthenticated && <NotificationBell />}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>

            {/* User Menu or Auth Buttons */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-trigger">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className={getAvatarColor(user?.id || '')}>
                        {getInitials(user?.username || '')}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-0.5">
                      <p className="text-sm font-medium">{user?.username}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <User className="mr-2 h-4 w-4" />
                    {t('nav_dashboard', language)}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/campaigns')} data-testid="menu-campaigns">
                    <Scroll className="mr-2 h-4 w-4" />
                    {t('nav_campaigns', language)}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="mr-2 h-4 w-4" />
                      {t('nav_admin', language)}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('logout', language)}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-1 sm:gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="nav-login" className="text-xs sm:text-sm">
                  {t('login', language)}
                </Button>
                <Button size="sm" onClick={() => navigate('/register')} data-testid="nav-register" className="text-xs sm:text-sm">
                  {t('register', language)}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;